import { Job, SheetData, Batch } from "../../pages/playground/types";
import "handsontable/styles/handsontable.css";
import "handsontable/styles/ht-theme-main.css";
import "handsontable/styles/ht-theme-horizon.css";
import { HotTable } from "@handsontable/react-wrapper";
import { useRef } from "react";
import { Box, Button, Container, Typography, Tooltip } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { registerAllModules } from "handsontable/registry";
import Handsontable from "handsontable";
import { getEnabledGroups } from "../../utils/content-groups";
import { formatPhrases } from "../../utils/content-group-formatting";
import { normalizeFieldName } from "../../utils/fieldNames";
registerAllModules();

export default function ClientBatchTable({
  jobs,
  sheetData,
  batch,
}: {
  jobs: Job[];
  sheetData: SheetData;
  batch?: Batch;
}) {
  const hotRef = useRef(null);
  const buttonClickCallback = () => {
    if (!hotRef.current) return;

    const hot = (hotRef.current as { hotInstance: Handsontable })?.hotInstance;
    const exportPlugin = hot?.getPlugin("exportFile");

    exportPlugin?.downloadFile("csv", {
      bom: false,
      columnDelimiter: ",",
      columnHeaders: true,
      exportHiddenColumns: true,
      exportHiddenRows: true,
      fileExtension: "csv",
      filename: `${batch?.name || "Batch"}_[YYYY]-[MM]-[DD]`,
      mimeType: "text/csv",
      rowDelimiter: "\r\n",
      rowHeaders: true,
    });
  };

  const idColumn = sheetData?.columns && sheetData?.columns[0];
  const enabledGroups = getEnabledGroups(batch?.settings);
  const exportRules = batch?.settings?.export_rules || [];

  // Build dynamic columns from enabled content groups + export rules.
  // Track existing column names to avoid clobbering master-file columns —
  // the Python export pipeline (master_file_sync.py) does the same dedup.
  const mergedColumns = [...(sheetData?.columns || [])];
  const existingColNames = new Set(mergedColumns);
  if (!existingColNames.has("status")) {
    mergedColumns.push("status");
    existingColNames.add("status");
  }

  // Collect group IDs consumed by export rules
  const mergedGroupIds = new Set(exportRules.flatMap((r) => r.sourceGroups));

  // Add individual group columns (skip those merged by a rule, and skip if
  // a master-file column already uses that normalized name).
  const groupColumns: { id: string; colName: string }[] = [];
  for (const group of enabledGroups) {
    if (mergedGroupIds.has(group.id)) continue;
    const colName = normalizeFieldName(group.label);
    if (existingColNames.has(colName)) continue;
    groupColumns.push({ id: group.id, colName });
    mergedColumns.push(colName);
    existingColNames.add(colName);
  }

  // Group lookup map for inner per-row loops (avoids enabledGroups.find on
  // every cell × row).
  const groupById = new Map(enabledGroups.map((g) => [g.id, g]));

  // Add export rule columns
  const ruleColumns: { rule: typeof exportRules[number]; colName: string }[] = [];
  for (const rule of exportRules) {
    if (!rule.label || rule.sourceGroups.length === 0) continue;
    const colName = normalizeFieldName(rule.label);
    if (existingColNames.has(colName)) continue;
    ruleColumns.push({ rule, colName });
    mergedColumns.push(colName);
    existingColNames.add(colName);
  }

  const sheetDataItems = sheetData?.items;

  // O(items × jobs) → O(items + jobs): build the master_id → Job map once and
  // look up by key. With 1000-job batches the previous .find() pattern was
  // ~1M comparisons per render.
  const jobByMasterId = new Map(jobs.map((j) => [String(j.master_id), j]));

  const relevantItems = sheetDataItems?.filter((item) =>
    jobByMasterId.has(String(item[idColumn || 0])),
  );

  const mergedRows =
    relevantItems &&
    relevantItems.map((item) => {
      const job = jobByMasterId.get(String(item[idColumn || 0]));

      const row: Record<string, string> = {
        ...item,
        status: job?.status || "",
      };

      // Format individual group columns
      for (const { id, colName } of groupColumns) {
        const group = groupById.get(id);
        const phrases = (job?.phrases || [])
          .filter((p) => p.type === id)
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((p) => p.phrase);
        row[colName] = group && phrases.length > 0
          ? formatPhrases(phrases, group.formatting)
          : "";
      }

      // Format export rule columns (merge source groups)
      for (const { rule, colName } of ruleColumns) {
        const parts: string[] = [];
        for (const gid of rule.sourceGroups) {
          const group = groupById.get(gid);
          const phrases = (job?.phrases || [])
            .filter((p) => p.type === gid)
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((p) => p.phrase);
          if (group && phrases.length > 0) {
            parts.push(formatPhrases(phrases, group.formatting));
          }
        }
        row[colName] = parts.join("\n\n");
      }

      return row;
    });

  return (
    <>
      <Container sx={{ mb: 2 }}>
        <Box display="flex" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <Typography variant="h6">Read-only Excel Table</Typography>
            <Tooltip title="Experimental.">
              <WarningAmberIcon
                sx={{ color: "warning.main", ml: 1 }}
                fontSize="small"
              />
            </Tooltip>
          </Box>

          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={buttonClickCallback}
          >
            Download CSV
          </Button>
        </Box>
      </Container>

      <HotTable
        ref={hotRef}
        data={mergedRows}
        rowHeaders={true}
        colHeaders={mergedColumns}
        height="auto"
        autoWrapRow={true}
        autoWrapCol={true}
        readOnly={true}
        licenseKey="non-commercial-and-evaluation"
      />
    </>
  );
}
